import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Badge,
  Grid,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Search,
  ExpandMore,
  ExpandLess,
  Hotel,
  LocationOn,
  Business,
  KingBed,
  Domain,
  CheckBox,
  IndeterminateCheckBox,
  FilterList,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface PropertySelectionModalProps {
  open: boolean;
  onClose: () => void;
  trendReportId: string;
  onConfirm: (selection: string) => void;
  initialSelection?: string;
}

interface Property {
  propertyId: string;
  propertyName: string;
  hotelClass: string;
  propertyAddress: string;
  brand: string;
  parentCompany?: string;
  rooms: number;
  yearBuilt: number;
  numberOfStories: number;
  constrStatus: string;
  hotelOperator: string;
  ownerName?: string;
  submarketId?: string;
  submarketName?: string;
}

interface Submarket {
  submarketId: string | null;
  submarketName: string;
  propertyCount: number;
  totalRooms: number;
  brandCount: number;
  classCounts: {
    luxury: number;
    upperUpscale: number;
    upscale: number;
    upperMidscale: number;
    midscale: number;
    economy: number;
  };
}

const hotelClassColors: Record<string, string> = {
  'All': '#a6a6a6',           // RGB(166,166,166)
  'Luxury': '#003f9f',         // RGB(0,63,159)
  'Upper Upscale': '#16d9bc',  // RGB(22,217,188)
  'Upscale': '#0c7876',        // RGB(12,120,118)
  'Upper Midscale': '#32a8fd', // RGB(50,168,253)
  'Midscale': '#ea80ff',       // RGB(234,128,255)
  'Economy': '#7b2a5c',        // RGB(123,42,92)
  'Unclassified': '#808080'    // RGB(128,128,128)
};

export const PropertySelectionModal: React.FC<PropertySelectionModalProps> = ({
  open,
  onClose,
  trendReportId,
  onConfirm,
  initialSelection,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [tabValue, setTabValue] = useState(0);
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertiesByClass, setPropertiesByClass] = useState<Record<string, Property[]>>({});
  const [submarkets, setSubmarkets] = useState<Submarket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  const [selectedSubmarkets, setSelectedSubmarkets] = useState<Set<string>>(new Set());
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set());
  const [marketName, setMarketName] = useState('');
  const [submarketFilter, setSubmarketFilter] = useState<string>('all');

  // Warning modal states
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({
    open: false,
    title: '',
    message: '',
  });

  // Fetch data when modal opens
  useEffect(() => {
    if (open && trendReportId) {
      fetchData();
    }
  }, [open, trendReportId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch properties
      const propertiesResponse = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/market-properties`,
        { method: 'GET' }
      );

      if (propertiesResponse?.ok) {
        const propertiesData = await propertiesResponse.json();
        console.log('Properties data received:', propertiesData); // Debug log
        setProperties(propertiesData.properties || []);
        setPropertiesByClass(propertiesData.propertiesByClass || {});
        setMarketName(propertiesData.marketName || '');
      } else {
        console.error('Properties response not ok:', propertiesResponse?.status);
      }

      // Fetch submarkets
      const submarketsResponse = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/submarkets`,
        { method: 'GET' }
      );

      if (submarketsResponse?.ok) {
        const submarketsData = await submarketsResponse.json();
        console.log('Submarkets data received:', submarketsData); // Debug log
        setSubmarkets(submarketsData.submarkets || []);
      } else {
        console.error('Submarkets response not ok:', submarketsResponse?.status);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load properties and submarkets');
    } finally {
      setLoading(false);
    }
  };

  const toggleClass = (className: string) => {
    const newExpanded = new Set(expandedClasses);
    if (newExpanded.has(className)) {
      newExpanded.delete(className);
    } else {
      newExpanded.add(className);
    }
    setExpandedClasses(newExpanded);
  };

  const toggleAllInClass = (className: string) => {
    const classProperties = propertiesByClass[className] || [];
    const getPropertyKey = (p: Property) => p.propertyId || `${p.propertyName}-${p.propertyAddress}`;
    const allSelected = classProperties.every(p => selectedProperties.has(getPropertyKey(p)));

    const newSelected = new Set(selectedProperties);
    if (allSelected) {
      classProperties.forEach(p => newSelected.delete(getPropertyKey(p)));
    } else {
      // Check if adding all would exceed the 20 property limit
      const newPropertiesToAdd = classProperties.filter(p => !newSelected.has(getPropertyKey(p)));
      if (newSelected.size + newPropertiesToAdd.length > 20) {
        setWarningDialog({
          open: true,
          title: 'Property Limit Exceeded',
          message: `Cannot select all properties in this class. You can select a maximum of 20 properties total. Currently selected: ${newSelected.size}`,
        });
        return;
      }

      // Add all properties
      classProperties.forEach(p => newSelected.add(getPropertyKey(p)));

      // Clear submarkets when selecting properties (only if multiple submarkets exist)
      if (selectedSubmarkets.size > 0 && submarkets.length > 1) {
        setSelectedSubmarkets(new Set());
      }
    }
    setSelectedProperties(newSelected);
  };

  const validateRoomDistribution = (selectedPropertyIds: Set<string>): { valid: boolean; errorMessage?: string } => {
    const getPropertyKey = (p: Property) => p.propertyId || `${p.propertyName}-${p.propertyAddress}`;
    const selectedProps = properties.filter(p => selectedPropertyIds.has(getPropertyKey(p)));

    if (selectedProps.length === 0) {
      return { valid: true };
    }

    // Calculate total rooms
    const totalRooms = selectedProps.reduce((sum, p) => sum + (p.rooms || 0), 0);

    if (totalRooms === 0) {
      return { valid: true };
    }

    // Check if any single property has more than 35% of rooms
    for (const prop of selectedProps) {
      const roomPercentage = ((prop.rooms || 0) / totalRooms) * 100;
      if (roomPercentage > 35) {
        return {
          valid: false,
          errorMessage: `Property "${prop.propertyName}" has ${prop.rooms} rooms (${roomPercentage.toFixed(1)}% of total), which exceeds the 35% limit. No single property can have more than 35% of the total rooms.`
        };
      }
    }

    // Check parent company distribution (excluding NULL parent companies)
    const parentCompanyRooms = new Map<string, number>();
    for (const prop of selectedProps) {
      if (prop.parentCompany) {
        const current = parentCompanyRooms.get(prop.parentCompany) || 0;
        parentCompanyRooms.set(prop.parentCompany, current + (prop.rooms || 0));
      }
    }

    for (const [company, rooms] of Array.from(parentCompanyRooms.entries())) {
      const roomPercentage = (rooms / totalRooms) * 100;
      if (roomPercentage > 35) {
        return {
          valid: false,
          errorMessage: `Parent company "${company}" has ${rooms} rooms (${roomPercentage.toFixed(1)}% of total), which exceeds the 35% limit. No single parent company can have more than 35% of the total rooms.`
        };
      }
    }

    return { valid: true };
  };

  const toggleProperty = (propertyId: string) => {
    const newSelected = new Set(selectedProperties);
    if (newSelected.has(propertyId)) {
      newSelected.delete(propertyId);
    } else {
      // Check if we're at the 20 property limit
      if (newSelected.size >= 20) {
        setWarningDialog({
          open: true,
          title: 'Property Limit Reached',
          message: 'You can select a maximum of 20 properties. Please deselect some properties before adding more.',
        });
        return;
      }
      newSelected.add(propertyId);
    }
    setSelectedProperties(newSelected);
    // Clear submarkets when selecting properties (only if multiple submarkets exist)
    if (selectedSubmarkets.size > 0 && submarkets.length > 1) {
      setSelectedSubmarkets(new Set());
    }
  };

  const toggleSubmarket = (submarketName: string) => {
    // Warn if properties are selected
    if (selectedProperties.size > 0) {
      setWarningDialog({
        open: true,
        title: 'Clear Selected Properties?',
        message: 'Selecting submarkets will clear all selected properties. Do you want to continue?',
        confirmText: 'Yes, Clear Properties',
        cancelText: 'Cancel',
        onConfirm: () => {
          setSelectedProperties(new Set());
          const newSelected = new Set(selectedSubmarkets);
          if (newSelected.has(submarketName)) {
            newSelected.delete(submarketName);
          } else {
            newSelected.add(submarketName);
          }
          setSelectedSubmarkets(newSelected);
        }
      });
      return;
    }

    // Toggle the submarket selection
    const newSelected = new Set(selectedSubmarkets);
    if (newSelected.has(submarketName)) {
      newSelected.delete(submarketName);
    } else {
      newSelected.add(submarketName);
    }
    setSelectedSubmarkets(newSelected);
  };

  const getFilteredProperties = () => {
    let filtered = properties;

    // Filter by search term - only hotel name or address
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(p =>
        p.propertyName.toLowerCase().includes(searchLower) ||
        p.propertyAddress?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by submarket if selected
    if (submarketFilter !== 'all') {
      filtered = filtered.filter(p => p.submarketName === submarketFilter);
    }

    return filtered;
  };

  const formatSelection = () => {
    // Check if properties are selected
    if (selectedProperties.size > 0) {
      const getPropertyKey = (p: Property) => p.propertyId || `${p.propertyName}-${p.propertyAddress}`;
      const selectedProps = properties.filter(p => selectedProperties.has(getPropertyKey(p)));

      const formatted = selectedProps.map((p, index) =>
        `${index + 1}. ${p.propertyName} - ${p.propertyAddress} - ${p.rooms} rooms${p.parentCompany ? ` - ${p.parentCompany}` : ''}${p.brand ? ` - ${p.brand}` : ''}`
      ).join('\n');

      return `SELECTED PROPERTIES (${selectedProps.length} hotels):\n\n${formatted}`;
    }

    // Check if submarkets are selected
    if (selectedSubmarkets.size > 0) {
      const selectedSubs = submarkets.filter(s => selectedSubmarkets.has(s.submarketName));

      const formatted = selectedSubs.map((s, index) =>
        `${index + 1}. ${s.submarketName} - ${s.propertyCount} hotels, ${s.totalRooms} total rooms`
      ).join('\n');

      return `SELECTED SUBMARKETS (${selectedSubs.length}):\n\n${formatted}`;
    }

    setWarningDialog({
      open: true,
      title: 'No Selection',
      message: 'Please select either properties (max 20) or submarkets',
    });
    return null;
  };

  const handleConfirm = () => {
    const selection = formatSelection();
    if (selection) {
      onConfirm(selection);
      onClose();
    }
  };

  const getPropertyKey = (p: Property) => p.propertyId || `${p.propertyName}-${p.propertyAddress}`;

  const totalRoomsSelected = properties
    .filter(p => selectedProperties.has(getPropertyKey(p)))
    .reduce((sum, p) => sum + (p.rooms || 0), 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Select Properties for {marketName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {submarkets.length > 1
            ? 'Choose either individual properties (max 20) OR submarkets (cannot mix both)'
            : 'Choose individual properties (max 20)'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {(selectedProperties.size > 0 && selectedSubmarkets.size > 0 && submarkets.length > 1) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Selection Conflict:</strong> You cannot mix properties and submarkets. Please clear one selection before proceeding.
              </Alert>
            )}

            {submarkets.length > 1 ? (
              <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} sx={{ mb: 2, flexShrink: 0 }}>
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Properties
                      <Badge badgeContent={selectedProperties.size} color="primary" />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Submarkets
                      <Badge badgeContent={selectedSubmarkets.size} color="primary" />
                    </Box>
                  }
                />
              </Tabs>
            ) : null}

            {/* Properties Tab - Show when tab is 0 or when there's only 1 or no submarkets */}
            {(tabValue === 0 || submarkets.length <= 1) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by hotel name or address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Submarket"
                    value={submarketFilter}
                    onChange={(e) => setSubmarketFilter(e.target.value)}
                    sx={{ minWidth: 200 }}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="all">All Submarkets</option>
                    {submarkets.map(s => (
                      <option key={s.submarketId || s.submarketName} value={s.submarketName}>
                        {s.submarketName} ({s.propertyCount})
                      </option>
                    ))}
                  </TextField>
                </Box>


                {/* Room Distribution Analysis */}
                {(() => {
                  if (selectedProperties.size === 0) {
                    return (
                      <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold" gutterBottom>
                            Room Distribution Requirements
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            To obtain the required data from CoStar, your property selection must meet the following criteria. If additional requirements arise, we'll contact you to discuss the best path forward.
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              • No single property can have more than 35% of the total rooms
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              • No single parent company can have more than 35% of the total rooms
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                            Select properties to see distribution analysis
                          </Typography>
                        </Box>
                      </Alert>
                    );
                  }


                  const selectedProps = properties.filter(p => selectedProperties.has(getPropertyKey(p)));
                  const totalRooms = selectedProps.reduce((sum, p) => sum + (p.rooms || 0), 0);

                  // Calculate property with highest room percentage
                  let maxPropertyRooms = 0;
                  let maxPropertyName = '';
                  for (const prop of selectedProps) {
                    if ((prop.rooms || 0) > maxPropertyRooms) {
                      maxPropertyRooms = prop.rooms || 0;
                      maxPropertyName = prop.propertyName;
                    }
                  }
                  const maxPropertyPercentage = totalRooms > 0 ? (maxPropertyRooms / totalRooms) * 100 : 0;

                  // Calculate parent company distribution
                  const parentCompanyRooms = new Map<string, number>();
                  for (const prop of selectedProps) {
                    if (prop.parentCompany) {
                      const current = parentCompanyRooms.get(prop.parentCompany) || 0;
                      parentCompanyRooms.set(prop.parentCompany, current + (prop.rooms || 0));
                    }
                  }

                  let maxCompanyRooms = 0;
                  let maxCompanyName = '';
                  for (const [company, rooms] of Array.from(parentCompanyRooms.entries())) {
                    if (rooms > maxCompanyRooms) {
                      maxCompanyRooms = rooms;
                      maxCompanyName = company;
                    }
                  }
                  const maxCompanyPercentage = totalRooms > 0 ? (maxCompanyRooms / totalRooms) * 100 : 0;

                  const propertyExceedsLimit = maxPropertyPercentage > 35;
                  const companyExceedsLimit = maxCompanyPercentage > 35;
                  const allCriteriaMet = !propertyExceedsLimit && !companyExceedsLimit;

                  return (
                    <Alert
                      severity={allCriteriaMet ? "success" : propertyExceedsLimit || companyExceedsLimit ? "warning" : "info"}
                      sx={{ mb: 2, flexShrink: 0 }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Room Distribution Analysis
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          To obtain the required data from CoStar, your property selection must meet the following criteria. If additional requirements arise, we'll contact you to discuss the best path forward.
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="body2"
                            color={allCriteriaMet ? "success.main" : propertyExceedsLimit ? "error.main" : "text.secondary"}
                            sx={{ mb: 0.5 }}
                          >
                            • Highest single property: "{maxPropertyName}" - {maxPropertyRooms} rooms ({maxPropertyPercentage.toFixed(1)}%)
                            {propertyExceedsLimit ? " - Exceeds 35% limit" : " ✓"}
                          </Typography>
                          {maxCompanyName && (
                            <Typography
                              variant="body2"
                              color={allCriteriaMet ? "success.main" : companyExceedsLimit ? "error.main" : "text.secondary"}
                            >
                              • Highest parent company: "{maxCompanyName}" - {maxCompanyRooms} rooms ({maxCompanyPercentage.toFixed(1)}%)
                              {companyExceedsLimit ? " - Exceeds 35% limit" : " ✓"}
                            </Typography>
                          )}
                          {!maxCompanyName && (
                            <Typography
                              variant="body2"
                              color={allCriteriaMet ? "success.main" : "text.secondary"}
                            >
                              • No parent company concentration (Unknown owners excluded)
                            </Typography>
                          )}
                        </Box>
                        {allCriteriaMet && (
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold', color: 'success.main' }}>
                            ✓ All distribution requirements met
                          </Typography>
                        )}
                        {(propertyExceedsLimit || companyExceedsLimit) && (
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                            Distribution criteria not met - Consider adjusting your selection
                          </Typography>
                        )}
                      </Box>
                    </Alert>
                  );
                })()}

                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  {(() => {
                    const filteredProperties = getFilteredProperties();

                    if (filteredProperties.length === 0 && searchTerm) {
                      return (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <Typography variant="body1" color="text.secondary">
                            No properties found matching "{searchTerm}"
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Try adjusting your search terms or clearing the search box
                          </Typography>
                        </Box>
                      );
                    }

                    if (properties.length === 0) {
                      return (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <Typography variant="body1" color="text.secondary">
                            No properties available for this market
                          </Typography>
                        </Box>
                      );
                    }

                    // If no filtered properties after applying filters
                    if (filteredProperties.length === 0) {
                      return (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <Typography variant="body1" color="text.secondary">
                            No properties match the current filters
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Try clearing the search or changing the submarket filter
                          </Typography>
                        </Box>
                      );
                    }

                    return Object.entries(propertiesByClass).map(([className, classProps]) => {
                      // Filter class properties based on filtered properties
                      const filteredPropertyKeys = new Set(filteredProperties.map(p => getPropertyKey(p)));
                      const filteredClassProps = classProps.filter(p =>
                        filteredPropertyKeys.has(getPropertyKey(p))
                      );

                      if (filteredClassProps.length === 0) return null;

                      const allSelected = filteredClassProps.every(p => selectedProperties.has(getPropertyKey(p)));
                      const someSelected = filteredClassProps.some(p => selectedProperties.has(getPropertyKey(p)));

                    return (
                      <Paper key={className} sx={{ mb: 2, p: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            p: 1,
                          }}
                          onClick={() => toggleClass(className)}
                        >
                          <IconButton size="small">
                            {expandedClasses.has(className) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <Checkbox
                            checked={allSelected}
                            indeterminate={!allSelected && someSelected}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAllInClass(className);
                            }}
                          />
                          <Chip
                            label={className}
                            size="small"
                            sx={{
                              bgcolor: hotelClassColors[className] || hotelClassColors['Unclassified'],
                              color: 'white',
                              fontWeight: 'bold',
                              mr: 2,
                            }}
                          />
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {filteredClassProps.length} properties • {filteredClassProps.reduce((sum, p) => sum + (p.rooms || 0), 0)} rooms
                          </Typography>
                        </Box>

                        <Collapse in={expandedClasses.has(className)}>
                          <List dense sx={{ pl: 4 }}>
                            {filteredClassProps.map(property => {
                              const propertyKey = property.propertyId || `${property.propertyName}-${property.propertyAddress}`;
                              return (
                              <ListItem key={propertyKey} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Checkbox
                                    edge="start"
                                    checked={selectedProperties.has(propertyKey)}
                                    onChange={() => toggleProperty(propertyKey)}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" fontWeight="medium">
                                        {property.propertyName}
                                      </Typography>
                                      {property.parentCompany && (
                                        <Chip
                                          label={property.parentCompany}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            borderColor: 'rgb(123,42,92)',
                                            color: 'rgb(123,42,92)',
                                            '&:hover': {
                                              backgroundColor: 'rgba(123,42,92,0.08)'
                                            }
                                          }}
                                        />
                                      )}
                                      {property.brand && (
                                        <Chip
                                          label={property.brand}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            borderColor: 'rgb(0,80,173)',
                                            color: 'rgb(0,80,173)',
                                            '&:hover': {
                                              backgroundColor: 'rgba(0,80,173,0.08)'
                                            }
                                          }}
                                        />
                                      )}
                                      <Chip
                                        label={`${property.rooms} rooms`}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          backgroundColor: 'white',
                                          color: 'rgb(12, 120, 118)',
                                          borderColor: 'rgb(12, 120, 118)'
                                        }}
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Box>
                                      <Typography variant="caption" component="span">
                                        {property.propertyAddress}
                                      </Typography>
                                      {property.submarketName && (
                                        <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                                          • {property.submarketName}
                                        </Typography>
                                      )}
                                      {property.hotelOperator && (
                                        <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                                          • Operated by {property.hotelOperator}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                              );
                            })}
                          </List>
                        </Collapse>
                      </Paper>
                    );
                  });
                })()}
                </Box>
              </Box>
            )}

            {/* Submarkets Tab - Only show when there are multiple submarkets */}
            {tabValue === 1 && submarkets.length > 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexShrink: 0 }}>
                  <Alert severity="info" sx={{ flex: 1 }}>
                    {selectedSubmarkets.size > 0
                      ? `${selectedSubmarkets.size} submarket${selectedSubmarkets.size > 1 ? 's' : ''} selected`
                      : '0 submarkets selected'
                    }
                    {selectedProperties.size > 0 && ' • Note: Properties will be cleared if you select submarkets'}
                  </Alert>
                  {selectedSubmarkets.size > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSelectedSubmarkets(new Set())}
                      color="error"
                    >
                      Clear All
                    </Button>
                  )}
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  <Grid container spacing={2}>
                  {submarkets.map(submarket => (
                    <Grid item xs={12} md={6} key={submarket.submarketId || submarket.submarketName}>
                      <Card
                        sx={{
                          border: selectedSubmarkets.has(submarket.submarketName)
                            ? '2px solid primary.main'
                            : '1px solid',
                          borderColor: selectedSubmarkets.has(submarket.submarketName)
                            ? 'primary.main'
                            : 'divider',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 2,
                          },
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Checkbox
                              checked={selectedSubmarkets.has(submarket.submarketName)}
                              onChange={() => toggleSubmarket(submarket.submarketName)}
                            />
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                              {submarket.submarketName}
                            </Typography>
                          </Box>

                          <Grid container spacing={1} sx={{ mb: 1 }}>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary">Properties</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {submarket.propertyCount}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary">Total Rooms</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {submarket.totalRooms.toLocaleString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary">Brands</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {submarket.brandCount}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Typography variant="caption" color="text.secondary">Hotel Classes:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {Object.entries(submarket.classCounts).map(([className, count]) => {
                              if (count === 0) return null;
                              const displayName = className.charAt(0).toUpperCase() + className.slice(1).replace(/([A-Z])/g, ' $1');
                              return (
                                <Chip
                                  key={className}
                                  label={`${displayName}: ${count}`}
                                  size="small"
                                  sx={{
                                    bgcolor: hotelClassColors[displayName] || hotelClassColors['Unclassified'],
                                    color: 'white',
                                    fontSize: '0.7rem',
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  </Grid>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          {selectedProperties.size > 0 && (
            <Button
              onClick={() => setSelectedProperties(new Set())}
              color="error"
            >
              Clear All
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={selectedProperties.size === 0 && selectedSubmarkets.size === 0}
          >
            Confirm Selection {
              selectedProperties.size > 0
                ? `(${selectedProperties.size} properties)`
                : selectedSubmarkets.size > 0
                  ? `(${selectedSubmarkets.size} submarket${selectedSubmarkets.size > 1 ? 's' : ''})`
                  : ''
            }
          </Button>
        </Box>
      </DialogActions>

      {/* Warning Dialog */}
      <Dialog
        open={warningDialog.open}
        onClose={() => setWarningDialog({ ...warningDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{warningDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{warningDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setWarningDialog({ ...warningDialog, open: false })}
          >
            {warningDialog.cancelText || 'OK'}
          </Button>
          {warningDialog.onConfirm && (
            <Button
              onClick={() => {
                warningDialog.onConfirm!();
                setWarningDialog({ ...warningDialog, open: false });
              }}
              variant="contained"
              color="primary"
            >
              {warningDialog.confirmText || 'Confirm'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};